import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Search, ClipboardList, Repeat } from 'lucide-react'
import { supabase, isDemoMode } from '../lib/supabase'
import { DemoNotice } from '../lib/demo'
import { reorderFromOrder } from '../lib/reorder'

const STATUS_STYLES = {
  draft:              'bg-gray-100 text-gray-700',
  awaiting_approval:  'bg-amber-50 text-amber-700',
  rejected:           'bg-red-50 text-red-700',
  sent:               'bg-brand-50 text-brand-dark',
  confirmed:          'bg-emerald-50 text-emerald-700',
  despatched:         'bg-sky-50 text-sky-700',
  cancelled:          'bg-red-50 text-red-700',
}
const STATUS_LABELS = {
  awaiting_approval: 'Awaiting approval',
  rejected: 'Rejected',
}

export default function Orders({ activeCompanyId = null, clientMode = false }) {
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [q, setQ] = useState('')
  const [reordering, setReordering] = useState(null) // id of the order being duplicated

  const handleReorder = async (e, orderId) => {
    e.preventDefault()
    e.stopPropagation()
    if (reordering) return
    setReordering(orderId)
    try {
      const { id } = await reorderFromOrder(orderId)
      navigate(clientMode ? `/my-orders/${id}` : `/orders/${id}`)
    } catch (err) {
      alert(`Reorder failed: ${err.message || err}`)
    } finally {
      setReordering(null)
    }
  }

  const load = async () => {
    if (isDemoMode) return
    setLoading(true)
    let query = supabase
      .from('orders')
      .select('id, reference, status, placed_at, created_at, customer_po, delivery_date_requested, companies(id, name)')
      .order('created_at', { ascending: false })
      .limit(200)
    // In client mode, scope to the active site (RLS already restricts to the
    // user's companies; this just keeps the UI focused on one site at a time).
    if (clientMode && activeCompanyId) query = query.eq('company_id', activeCompanyId)
    const { data } = await query
    setRows(data || [])
    setLoading(false)
  }
  useEffect(() => { load() }, [activeCompanyId, clientMode])

  const filtered = rows.filter(r => {
    const s = q.toLowerCase()
    return !s
      || (r.reference || '').toLowerCase().includes(s)
      || (r.companies?.name || '').toLowerCase().includes(s)
      || (r.customer_po || '').toLowerCase().includes(s)
  })

  return (
    <div className="space-y-4">
      <DemoNotice />

      <div className="card">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search by reference, company, PO…" className="input-field pl-9"
            />
          </div>
          {!clientMode && (
            <button onClick={() => navigate('/orders/new')} className="btn-primary">
              <Plus className="w-4 h-4" /> New Order
            </button>
          )}
          {clientMode && (
            <button onClick={() => navigate('/place-order')} className="btn-primary">
              <Plus className="w-4 h-4" /> Place new order
            </button>
          )}
        </div>

        {loading && <p className="text-sm text-gray-500">Loading…</p>}

        {!loading && filtered.length === 0 && (
          <p className="text-sm text-gray-500 py-6 text-center">
            {rows.length === 0 ? 'No orders yet.' : 'No matches.'}
          </p>
        )}

        {filtered.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {filtered.map(o => (
              <li key={o.id}>
                <Link to={clientMode ? `/my-orders/${o.id}` : `/orders/${o.id}`} className="flex items-center gap-3 py-3 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors">
                  <ClipboardList className="w-5 h-5 text-brand" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-800 truncate">
                      {o.reference || '(draft)'} — {o.companies?.name || '—'}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {o.customer_po ? `PO ${o.customer_po} · ` : ''}
                      Requested {o.delivery_date_requested || '—'}
                    </div>
                  </div>
                  {o.status !== 'draft' && (
                    <button
                      onClick={(e) => handleReorder(e, o.id)}
                      disabled={reordering === o.id}
                      title="Create a new draft with the same items"
                      className="text-xs px-2.5 py-1 rounded-full bg-white border border-brand text-brand-dark hover:bg-brand-50 disabled:opacity-50 inline-flex items-center gap-1 transition-colors"
                    >
                      <Repeat className="w-3.5 h-3.5" />
                      {reordering === o.id ? 'Copying…' : 'Reorder'}
                    </button>
                  )}
                  <span className={`text-xs px-2 py-1 rounded-full ${STATUS_STYLES[o.status] || 'bg-gray-100 text-gray-700'}`}>
                    {STATUS_LABELS[o.status] || o.status}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
